import { useEffect, useState } from "react";
import { useT } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateInput } from "@/components/DateInput";
import { fmtMoney } from "@/lib/time";
import { formatDate } from "@/lib/dates";
import { Search, FileDown, Ban, CheckCircle2, Trash2, Plus, Printer, Eye } from "lucide-react";

const STATUS_CLS = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Issued: "bg-green-50 text-green-700 border-green-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function Receipts() {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invSearch, setInvSearch] = useState("");
  const [selectedInv, setSelectedInv] = useState(null);
  const [invSummary, setInvSummary] = useState(null);
  const [payDate, setPayDate] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/receipts", { params })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, [q, status, dateFrom, dateTo]);

  const pdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/receipts/${id}/pdf`;

  const resetNew = () => {
    setInvSearch("");
    setSelectedInv(null);
    setInvSummary(null);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayAmount("");
    setPayNote("");
  };

  const openNew = () => {
    resetNew();
    setNewOpen(true);
    api.get("/invoices")
      .then((r) => setInvoices(r.data.filter((i) => i.status === "Issued")))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const selectInvoice = (inv) => {
    setSelectedInv(inv);
    setInvSummary(null);
    api.get(`/invoices/${inv.id}/payments`)
      .then((r) => {
        setInvSummary(r.data);
        setPayAmount(r.data.outstanding > 0 ? String(r.data.outstanding) : "");
      })
      .catch(() => {});
  };

  const submitNew = async () => {
    if (!selectedInv || !payAmount) return;
    setBusy(true);
    try {
      const p = await api.post(`/invoices/${selectedInv.id}/payments`, {
        payment_date: payDate,
        amount: parseFloat(payAmount),
        method: "cash",
        note: payNote,
        issue_receipt: true,
      });
      toast.success(t("receipts.created"));
      const rcs = await api.get("/receipts", { params: { invoice_id: selectedInv.id } });
      const rc = rcs.data.find((r) => r.payment_id === p.data.id);
      setNewOpen(false);
      resetNew();
      load();
      if (rc) setPreview(rc);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const issue = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/receipts/${id}/issue`);
      toast.success(t("receipts.issued"));
      load();
      if (preview && preview.id === id) setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    const reason = window.prompt(t("receipts.cancel_prompt"));
    if (reason === null) return;
    setBusy(true);
    try {
      const r = await api.post(`/receipts/${id}/cancel`, { reason });
      toast.success(t("receipts.cancelled"));
      load();
      if (preview && preview.id === id) setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("receipts.delete_draft_confirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/receipts/${id}`);
      toast.success(t("receipts.deleted"));
      if (preview && preview.id === id) setPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const filteredInvoices = invoices.filter((i) =>
    !invSearch || `${i.invoice_number} ${i.client}`.toLowerCase().includes(invSearch.toLowerCase()));

  const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 break-words">{children || "—"}</div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto lg:p-6" data-testid="receipts-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <h1 className="font-serif text-2xl text-slate-800">{t("receipts.title")}</h1>
        <Button data-testid="new-receipt-button" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1.5" /> {t("receipts.new")}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="receipts-search-input" className="pl-8" placeholder={t("receipts.search")}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="receipts-status-filter" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="receipts-status-all">{t("receipts.all_statuses")}</SelectItem>
            <SelectItem value="Draft" data-testid="receipts-status-draft">{t("val.Draft")}</SelectItem>
            <SelectItem value="Issued" data-testid="receipts-status-issued">{t("val.Issued")}</SelectItem>
            <SelectItem value="Cancelled" data-testid="receipts-status-cancelled">{t("val.Cancelled")}</SelectItem>
          </SelectContent>
        </Select>
        <DateInput testid="receipts-date-from" value={dateFrom} onChange={(v) => setDateFrom(v || "")} />
        <DateInput testid="receipts-date-to" value={dateTo} onChange={(v) => setDateTo(v || "")} />
      </div>

      {rows === null ? (
        <p className="text-sm text-slate-400">…</p>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-10 text-center" data-testid="receipts-empty">
          <p className="text-sm text-slate-500 mb-4">{t("receipts.empty")}</p>
          <Button data-testid="receipts-empty-new-button" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-1.5" /> {t("receipts.new")}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">{t("receipts.number")}</th>
                <th className="py-2 pr-4">{t("receipts.date")}</th>
                <th className="py-2 pr-4">{t("receipts.client")}</th>
                <th className="py-2 pr-4">{t("receipts.invoice")}</th>
                <th className="py-2 pr-4 text-right">{t("receipts.amount")}</th>
                <th className="py-2 pr-4">{t("receipts.status")}</th>
                <th className="py-2 text-right">{t("receipts.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`receipt-row-${r.id}`} onClick={() => setPreview(r)}>
                  <td className="py-2.5 pr-4 font-semibold text-slate-800" data-testid={`receipt-number-${r.id}`}>
                    {r.receipt_number || "—"}
                  </td>
                  <td className="py-2.5 pr-4 text-slate-600">{formatDate(r.payment_date)}</td>
                  <td className="py-2.5 pr-4 text-slate-600 max-w-52 truncate">{r.client}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.invoice_number}</td>
                  <td className="py-2.5 pr-4 text-right text-slate-800">{fmtMoney(r.amount)}</td>
                  <td className="py-2.5 pr-4">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${STATUS_CLS[r.status] || ""}`}
                      data-testid={`receipt-status-${r.id}`}>{t(`val.${r.status}`)}</span>
                  </td>
                  <td className="py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <button data-testid={`receipt-view-${r.id}`} onClick={() => setPreview(r)}
                        title={t("receipts.view")}
                        className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors">
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      {r.status === "Draft" && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`receipt-issue-${r.id}`} disabled={busy}
                            onClick={() => issue(r.id)} className="h-8">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("receipts.issue")}
                          </Button>
                          <button data-testid={`receipt-delete-${r.id}`} onClick={() => del(r.id)}
                            className="p-1.5 text-slate-400 hover:text-red-700 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {r.status === "Issued" && (
                        <Button size="sm" variant="outline" data-testid={`receipt-cancel-${r.id}`} disabled={busy}
                          onClick={() => cancel(r.id)}
                          className="h-8 text-red-700 border-red-200 hover:bg-red-50">
                          <Ban className="h-3.5 w-3.5 mr-1" /> {t("receipts.cancel")}
                        </Button>
                      )}
                      {r.status !== "Draft" && (
                        <a href={pdfUrl(r.id)} target="_blank" rel="noreferrer" data-testid={`receipt-pdf-${r.id}`}
                          className="inline-flex items-center h-8 px-2.5 text-xs rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors">
                          <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg" data-testid="new-receipt-dialog">
          <DialogHeader>
            <DialogTitle>{t("receipts.new_title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1">{t("receipts.select_invoice")}</div>
              <Input data-testid="receipt-invoice-search" placeholder={t("receipts.search_invoice")}
                value={invSearch} onChange={(e) => setInvSearch(e.target.value)} className="h-9 text-sm" />
              {!selectedInv && (
                <div className="mt-1.5 max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="receipt-invoice-results">
                  {filteredInvoices.length === 0 ? (
                    <p className="text-xs text-slate-400 p-3" data-testid="receipt-invoice-empty">{t("receipts.no_invoices")}</p>
                  ) : filteredInvoices.map((i) => (
                    <button key={i.id} data-testid={`receipt-invoice-option-${i.id}`}
                      onClick={() => selectInvoice(i)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors">
                      <span className="font-semibold text-slate-800">{i.invoice_number}</span>
                      <span className="text-slate-500"> · {i.client} · {fmtMoney(i.totals?.total ?? 0)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedInv && (
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="receipt-selected-invoice">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800">{selectedInv.invoice_number}</div>
                  <button className="text-xs text-slate-400 hover:text-slate-700" data-testid="receipt-change-invoice"
                    onClick={() => { setSelectedInv(null); setInvSummary(null); }}>
                    {t("receipts.change")}
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{selectedInv.client}</div>
                {invSummary && (
                  <div className="text-xs text-slate-600 mt-1.5" data-testid="receipt-outstanding-hint">
                    {t("receipts.outstanding")}: <span className="font-semibold">{fmtMoney(invSummary.outstanding)}</span>
                    {" · "}{t("receipts.invoice")}: {fmtMoney(invSummary.gross)}
                  </div>
                )}
              </div>
            )}
            {selectedInv && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">{t("receipts.payment_amount")}</div>
                    <Input data-testid="receipt-amount-input" type="number" step="0.01" min="0"
                      value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">{t("receipts.date")}</div>
                    <DateInput testid="receipt-date-input" value={payDate} onChange={(v) => setPayDate(v || "")} />
                  </div>
                </div>
                <Input data-testid="receipt-note-input" placeholder={t("pay.note")}
                  value={payNote} onChange={(e) => setPayNote(e.target.value)} className="h-9 text-sm" />
                <Button data-testid="receipt-submit-button" disabled={busy || !payAmount || parseFloat(payAmount) <= 0}
                  onClick={submitNew} className="w-full bg-slate-900 hover:bg-slate-800">
                  {t("receipts.register")}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="receipt-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t("receipts.preview")} {preview.receipt_number ? `· ${preview.receipt_number}` : `· ${t("val.Draft")}`}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("receipts.number")} testid="preview-number">{preview.receipt_number}</Field>
                <Field label={t("receipts.status")} testid="preview-status">{t(`val.${preview.status}`)}</Field>
                <Field label={t("receipts.date")} testid="preview-date">{formatDate(preview.payment_date)}</Field>
                <Field label={t("receipts.amount")} testid="preview-amount">{fmtMoney(preview.amount)}</Field>
                <Field label={t("receipts.payer")} testid="preview-payer">{preview.client}</Field>
                <Field label={t("receipts.invoice")} testid="preview-invoice">{preview.invoice_number}</Field>
                <Field label={t("receipts.method")} testid="preview-method">{t("pay.method_cash")}</Field>
                {preview.created_by && (
                  <Field label={t("receipts.created_by")} testid="preview-created-by">{preview.created_by}</Field>
                )}
                {preview.issued_at && (
                  <Field label={t("receipts.issued_at")} testid="preview-issued-at">{formatDate(preview.issued_at.slice(0, 10))}</Field>
                )}
                {preview.status === "Cancelled" && (
                  <Field label={t("receipts.cancelled_info")} testid="preview-cancelled-info">
                    {preview.cancelled_at ? formatDate(preview.cancelled_at.slice(0, 10)) : ""}{preview.cancel_reason ? ` — ${preview.cancel_reason}` : ""}
                  </Field>
                )}
              </div>
              {preview.status === "Draft" && (
                <p className="text-xs text-slate-500 mt-2" data-testid="preview-draft-hint">{t("receipts.draft_hint")}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                {preview.status === "Draft" && (
                  <>
                    <Button data-testid="receipt-preview-issue-button" disabled={busy}
                      onClick={() => issue(preview.id)} className="bg-slate-900 hover:bg-slate-800">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> {t("receipts.issue")}
                    </Button>
                    <Button variant="outline" data-testid="receipt-preview-delete-button" disabled={busy}
                      onClick={() => del(preview.id)}
                      className="text-red-700 border-red-200 hover:bg-red-50">
                      <Trash2 className="h-4 w-4 mr-1.5" /> {t("receipts.delete_draft")}
                    </Button>
                  </>
                )}
                {preview.status === "Issued" && (
                  <Button variant="outline" data-testid="receipt-preview-cancel-button" disabled={busy}
                    onClick={() => cancel(preview.id)} className="text-red-700 border-red-200 hover:bg-red-50">
                    <Ban className="h-4 w-4 mr-1.5" /> {t("receipts.cancel")}
                  </Button>
                )}
                {preview.status !== "Draft" && (
                  <>
                    <a href={pdfUrl(preview.id)} target="_blank" rel="noreferrer" data-testid="receipt-preview-pdf-button"
                      className="inline-flex items-center h-10 px-4 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                      <FileDown className="h-4 w-4 mr-1.5" /> PDF
                    </a>
                    <button data-testid="receipt-preview-print-button"
                      onClick={() => window.open(pdfUrl(preview.id), "_blank")}
                      className="inline-flex items-center h-10 px-4 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors">
                      <Printer className="h-4 w-4 mr-1.5" /> {t("receipts.print")}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
